import React from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import { useSelector } from "react-redux";
import { Login } from "./container/login/login";
import { Layout } from "./layout";
import AddNewTimeSheet from "./container/timesheet/addTimeSheet";
import UserTypeSelection from "./container/userType/userTypeSelection";
import Spinner from "./components/Spinner/spinner";
import EmptyState from "./components/EmptyState/emptyState";
import { TimeSheetTabView } from "./container/timeSheetList/timeSheetTabView";
import ApprovalTimeSheet from "./container/timesheet/approvalTimeSheet";
import TimeSheetList from "./container/timeSheetList/timeSheetList";
import EmployeeTimeSheet from "./container/timeSheetList/employeeTimeSheet";
import { AddEmployee } from "./container/Admin/Employees/AddEmployee";
import { AddProject } from "./container/Admin/Projects/AddProject";
import TimeSheetsAdmin from "./container/Admin/TimeSheets/TimeSheets";
import EmployeeList from "./container/Admin/Employees/EmployeeList";
import ProjectList from "./container/Admin/Projects/ProjectList";
import RouteGuard from "./RouteGuard";
import api from "./services/api";
import PageNotFound from "./container/noAccess/PageNotFound";
import TimesheetSummary from "./container/Admin/TimesheetSummary/TimesheetSummary"
const ProtectedRoute = ({ children }) => {
  const { userData, loading } = useSelector((state) => state?.user);
  const userType = localStorage.getItem("userType");

  if (loading) {
    return <Spinner />;
  }

  if (!userData) {
    return <Navigate to="/" replace />;
  }

  // For timesheet routes, ensure user type is selected
  if (window.location.pathname.startsWith("/*") && !userType) {
    // If multiple types available, go to selection
    if (userData.userTypes?.length > 1) {
      return <Navigate to="/select-user-type" replace />;
    }
    // If single type, use that
    if (userData.userTypes?.length === 1) {
      localStorage.setItem("userType", userData.userTypes[0].id.toString());
    }
  }

  return children;
};

const Routing = ({ isAuthenticated, token }) => {
  const { userData, loading } = useSelector((state) => state.user);
  const userType = localStorage.getItem("userType");
  const currentUserTypeId = localStorage.getItem("userTypeId");
  const currentUserRoleId = userData?.userTypes?.find(
    (type) => type?.userTypeId == currentUserTypeId
  );

  //help in find current role
  const userTypeRole = userData?.userTypes?.find((type) =>
    type?.userType?.toLowerCase()?.includes(userType?.toLowerCase())
  );
  const roleId = currentUserRoleId?.userTypeRoleId
    ? currentUserRoleId?.userTypeRoleId
    : userTypeRole?.userTypeRoleId;

  const isSuperAdmin = userTypeRole?.userTypeRoleId === 1;
  const isHr = [2].includes(userTypeRole?.userTypeRoleId);
  const isAccounts = [3].includes(userTypeRole?.userTypeRoleId);

  if (loading) {
    return <Spinner />;
  }

  return (
    <Routes>
      {/* Unauthenticated Users */}
      {!isAuthenticated && !token && (
        <>
          <Route
            path="/*"
            element={
              <UnauthenticatedTemplate>
                <Login />
              </UnauthenticatedTemplate>
            }
          />
        </>
      )}

      {/* Authenticated Users */}
      {isAuthenticated && token && userData && (
        <>
          <Route
            path="/select-user-type"
            element={
              <AuthenticatedTemplate>
                <ProtectedRoute>
                  <UserTypeSelection />
                </ProtectedRoute>
              </AuthenticatedTemplate>
            }
          />
          <Route
            path="/*"
            element={
              <AuthenticatedTemplate>
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              </AuthenticatedTemplate>
            }
          >
            {/* User Routes */}
            <Route
              path="user/*"
              element={
                <Routes>
                  {/* manager(4)+employee(5) */}
                  <Route
                    path="add-timesheet"
                    element={
                      <RouteGuard
                        currentRoleId={roleId}
                        allowedRoleIds={[4, 5]}
                      >
                        <AddNewTimeSheet />
                      </RouteGuard>
                    }
                  />


                  <Route
                    path="edit-timesheet/:id"
                    element={
                      <RouteGuard
                        currentRoleId={roleId}
                        allowedRoleIds={[4, 5]}
                      >
                        <AddNewTimeSheet />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="self-timesheet/:id"
                    element={
                      <RouteGuard
                        currentRoleId={roleId}
                        allowedRoleIds={[4, 5]}
                      >
                        <ApprovalTimeSheet />
                      </RouteGuard>
                    }
                  />

                  {/* manager(4) hr(2) and account(3) */}
                  <Route
                    path="timesheet/:id"
                    element={
                      <RouteGuard
                        currentRoleId={roleId}
                        allowedRoleIds={[2, 3, 4]}
                      >
                        <ApprovalTimeSheet />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="timesheet"
                    element={
                      <RouteGuard
                        currentRoleId={roleId}
                        allowedRoleIds={[2, 3, 4, 5]}
                      >
                        <TimeSheetList />
                      </RouteGuard>
                    }
                  />


                  {/* hr(2) and account(3) */}
                  <Route
                    path="employees/timesheets"
                    element={
                      <RouteGuard
                        currentRoleId={roleId}
                        allowedRoleIds={[2, 3, 4]}
                      >
                        <EmployeeTimeSheet />
                      </RouteGuard>
                    }
                  />
                  {/* Catch-all route for user section */}
                  <Route
                    path="*"
                    element={<Navigate to="/page-not-found" replace />}
                  />
                </Routes>
              }
            />

            {/* Admin Routes */}
            <Route
              path="admin/*"
              element={
                <Routes>
                  <Route
                    path="timesheet-list"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[1]}>
                        <TimeSheetsAdmin />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="timesheet/:id"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[1]}>
                        <ApprovalTimeSheet />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="employee-list"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[1]}>
                        <EmployeeList />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="add-employee"
                    element={<Navigate to="employee/new" />}
                  />
                  <Route
                    path="employee/:id"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[1]}>
                        <AddEmployee />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="project-list"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[1]}>
                        <ProjectList />
                      </RouteGuard>
                    }
                  />
                           <Route
                    path="timesheet-summary"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[1]}>
                      <TimesheetSummary/>
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="add-project"
                    element={<Navigate to="admin/project/new" />}
                  />
                  <Route
                    path="project/:id"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[1]}>
                        <AddProject />
                      </RouteGuard>
                    }
                  />
                  {/* Catch-all route for admin section */}
                  <Route
                    path="*"
                    element={<Navigate to="/page-not-found" replace />}
                  />
                </Routes>
              }
            />

            {/* Hr routes */}
            {/* Admin Routes */}
            <Route
              path="hr/*"
              element={
                <Routes>
                  <Route
                    path="timesheet-list"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[2]}>
                        <TimeSheetsAdmin />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="employees/timesheets"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[2]}>
                        <EmployeeTimeSheet />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="timesheet/:id"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[2]}>
                        <ApprovalTimeSheet />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="employee-list"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[2]}>
                        <EmployeeList />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="timesheet-summary"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[2]}>
                      <TimesheetSummary/>
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="add-employee"
                    element={<Navigate to="employee/new" />}
                  />
                  <Route
                    path="employee/:id"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[2]}>
                        <AddEmployee />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="project-list"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[2]}>
                        <ProjectList />
                      </RouteGuard>
                    }
                  />
                  <Route
                    path="add-project"
                    element={<Navigate to="hr/project/new" />}
                  />
                  <Route
                    path="project/:id"
                    element={
                      <RouteGuard currentRoleId={roleId} allowedRoleIds={[2]}>
                        <AddProject />
                      </RouteGuard>
                    }
                  />
                  {/* Catch-all route for admin section */}
                  <Route
                    path="*"
                    element={<Navigate to="/page-not-found" replace />}
                  />
                </Routes>
              }
            />

            {/* Catch-all route for main section */}
            <Route
              path="*"
              element={<Navigate to="/page-not-found" replace />}
            />
          </Route>
          <Route
            path="/no-access"
            element={
              <AuthenticatedTemplate>
                <EmptyState />
              </AuthenticatedTemplate>
            }
          />
          <Route
            path="/page-not-found"
            element={
              <AuthenticatedTemplate>
                <PageNotFound />
              </AuthenticatedTemplate>
            }
          />
          <Route
            path="/"
            element={
              userData ? (
                <Navigate
                  to={
                    userData.userTypes?.length > 1
                      ? "/select-user-type"
                      : isSuperAdmin
                      ? "admin/timesheet-list"
                      : isAccounts
                      ? "user/employees/timesheets"
                      : isHr?"hr/employees/timesheets":"user/timesheet"
                  }
                  replace
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </>
      )}
    </Routes>
  );
};

export default Routing;
